/* eslint-disable import/prefer-default-export */

// maps a simple metadata key (from util/metadata createMetadataObject()) to the
// metadata bundle item variant.
//
// key: data.name, value: variant
const VARIANT_MAP = {
  title: 'default',
  description: 'default',
  thumbnail: 'default',
  robots: 'default',
  'qc:section': 'default',

  canonical: 'links',
  alternate: 'links',

  'og:title': 'opengraph',
  'og:description': 'opengraph',
  'og:image': 'opengraph',
  'og:url': 'opengraph',
  'og:type': 'opengraph',
  'og:site_name': 'opengraph',

  'twitter:card': 'twitter',
  'twitter:title': 'twitter',
  'twitter:description': 'twitter',
  'twitter:image': 'twitter',
  'twitter:url': 'twitter',
  'twitter:site': 'twitter',

  'flipboard-title': 'flipboard',
  'flipboard-article': 'flipboard',
  'flipboard-image': 'flipboard',
  'flipboard-author': 'flipboard',
  'flipboard-date': 'flipboard',

  'application/ld+json': 'scripts',

  prismColors: 'hidden',
  prismTags: 'hidden'
};

/**
 * returns a metadata bundle item from the provided `name` and `value`.
 *
 * the `variant` of the item is determined by the `VARIANT_MAP` constant, but
 * can be overridden by passing a third `variantOverride` parameter.
 *
 * @export
 * @param {String} name
 * @param {String} value
 * @param {String} [variantOverride] optional, specifies the exact variant to use
 * @returns {Object} bundle item
 */
export function generateMetadataBundleItem(name, value, variantOverride) {
  const variant = variantOverride || VARIANT_MAP[name];

  // skip unknown variants; they're not intended for the <head>
  if (!variant) {
    return null;
  }

  return {
    id: `meta-${variant}-${name}`,
    type: 'metadata',
    variant,
    data: {
      name,
      value
    }
  };
}

/**
 * returns an array of bundle items from the provided metadata object (created
 * via `createMetadataObject()`).
 *
 * a `title` is an essential attribute to pass for various reasons. without it
 * we will lack the requisite bundle item to provide a <title> for the page.
 *
 * @export
 * @param {Object} metadata
 * @param {String} metadata.title page title
 * @returns {Object[]} array of metadata items with the special-case title item first
 */
export function generateMetadataBundleItems(metadata = {}) {
  const initialItems = [];

  try {
    if (metadata.title) {
      // we have a special case for the title metadata item: we need to generate
      // a duplicate 'title' metadata bundle item, because it's used for the
      // page <title>.
      initialItems.push(
        generateMetadataBundleItem('title', metadata.title, 'title')
      );
    } else {
      throw new Error("Missing required 'title' metadata prop.");
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e.message);
  }

  const items = Object.entries(metadata).reduce((acc, [k, v]) => {
    const item = generateMetadataBundleItem(k, v);
    return item ? acc.concat(item) : acc;
  }, initialItems);

  return items;
}

/**
 * simple convenience method to wrap up the creation of a full metadata bundle
 * in one fell swoop.
 *
 * @export
 * @param {Object[]} [bundleItems=[]] metadata bundle items
 * @returns {Object} fully-formed metadata bundle
 */
export function generateMetadataBundle(bundleItems = []) {
  const bundle = {
    id: 'meta',
    layout: 'metadata',
    items: [...bundleItems]
  };

  return bundle;
}

/**
 * convenience method to return a full set of page bundles (only metadata
 * layout) for a given metadata object.
 *
 * @export
 * @param {Object} metadata metadata object returned from createMetadataObject()
 * @returns {Object[]} array of page bundles
 */
export function generatePageBundlesFromMetadata(metadata = {}) {
  return [generateMetadataBundle(generateMetadataBundleItems(metadata))];
}
